import { lazy, type ComponentType } from "react";
import { createBrowserRouter, createMemoryRouter, type RouteObject } from "react-router-dom";

import { App } from "./App.js";

// Phase 4.1 — route-level code splitting. Every page lives in its own chunk so
// the initial bundle only ships the shell + the landing page (`HomePage`).
// Suspense boundary is provided by App.tsx around <Outlet />.
//
// `withDefault` lets a page module use either `export function MyPage()` or
// `export default MyPage`. Existing pages use named exports so we adapt them
// here without rewriting every page file.
function withDefault<TProps>(
  importer: () => Promise<Record<string, ComponentType<TProps>>>,
  exportName: string,
): () => Promise<{ default: ComponentType<TProps> }> {
  return async () => {
    const mod = await importer();
    const Component = mod[exportName] ?? mod["default"];
    if (!Component) {
      throw new Error(`router: page module is missing export "${exportName}"`);
    }
    return { default: Component };
  };
}

const HomePage = lazy(withDefault(() => import("@/pages/HomePage.js"), "HomePage"));
const MePage = lazy(withDefault(() => import("@/pages/MePage.js"), "MePage"));
const MenuPage = lazy(withDefault(() => import("@/pages/MenuPage.js"), "MenuPage"));
const BoardPage = lazy(withDefault(() => import("@/pages/BoardPage.js"), "BoardPage"));
const PostDetailPage = lazy(
  withDefault(() => import("@/pages/PostDetailPage.js"), "PostDetailPage"),
);
const ProfilePage = lazy(withDefault(() => import("@/pages/ProfilePage.js"), "ProfilePage"));
const SearchPage = lazy(withDefault(() => import("@/pages/SearchPage.js"), "SearchPage"));
const SettingsPage = lazy(withDefault(() => import("@/pages/SettingsPage.js"), "SettingsPage"));
const GovernancePage = lazy(
  withDefault(() => import("@/pages/GovernancePage.js"), "GovernancePage"),
);
const NotFoundPage = lazy(withDefault(() => import("@/pages/NotFoundPage.js"), "NotFoundPage"));

export const routes: RouteObject[] = [
  {
    path: "/",
    element: <App />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "me", element: <MePage /> },
      { path: "menu", element: <MenuPage /> },
      { path: "b/:boardId", element: <BoardPage /> },
      { path: "p/:postId", element: <PostDetailPage /> },
      { path: "u/:userId", element: <ProfilePage /> },
      { path: "search", element: <SearchPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "governance", element: <GovernancePage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);

export function createAppRouter(initialEntries: string[] = ["/"]) {
  return createMemoryRouter(routes, { initialEntries });
}
